import api from './client'
import type { SuggestedAction } from './plugins'

export interface ChatMessage {
  role: 'user' | 'assistant'
  content: string
  tool_calls?: ToolCall[]
  suggested_actions?: SuggestedAction[]
  timestamp?: string
}

export interface ToolCall {
  name: string
  arguments: Record<string, unknown>
  result?: unknown
  audit_log_id?: number
}

export interface SendMessageRequest {
  message: string
  session_id?: string
}

export interface SendMessageResponse {
  ok: boolean
  session_id: string
  response: string
  message: ChatMessage
  tool_calls?: ToolCall[]
  suggested_actions?: SuggestedAction[]
  turn_count?: number
}

export interface SessionInfo {
  id: string
  created_at: string
  updated_at: string
  message_count: number
  summary?: string
}

export async function sendMessage(
  request: SendMessageRequest
): Promise<SendMessageResponse> {
  const response = await api.post<SendMessageResponse>('/chat/chat', request)
  return response.data
}

export async function getSessions(
  limit = 20,
  offset = 0
): Promise<SessionInfo[]> {
  const response = await api.get<SessionInfo[]>('/chat/sessions', {
    params: { limit, offset },
  })
  return response.data
}

export async function getSessionHistory(
  sessionId: string
): Promise<ChatMessage[]> {
  const response = await api.get<ChatMessage[]>(`/chat/sessions/${sessionId}`)
  return response.data
}

export async function deleteSession(sessionId: string): Promise<void> {
  await api.delete(`/chat/sessions/${sessionId}`)
}

export interface CreateSessionResponse {
  ok: boolean
  session_id: string
  created_at: string
}

/**
 * Create a new chat session.
 * This allows connecting to SSE before sending the first message,
 * enabling real-time streaming from the very first message.
 */
export async function createSession(): Promise<CreateSessionResponse> {
  const response = await api.post<CreateSessionResponse>('/chat/sessions')
  return response.data
}

// Event types from backend
export interface StreamEvent {
  type: string
  data: Record<string, unknown>
  session_id?: string
  timestamp?: string
  event_id?: string
}

export interface StreamCallbacks {
  onStatus?: (message: string) => void
  onToolStart?: (name: string, args: Record<string, unknown>) => void
  onToolResult?: (name: string, result: unknown, success: boolean) => void
  onThought?: (thought: string) => void
  onResponseChunk?: (chunk: string) => void
  onComplete?: (response: SendMessageResponse) => void
  onError?: (error: Error) => void
}

/**
 * Send a chat message with real-time streaming of events.
 *
 * Creates a session first if needed, then connects to SSE for real-time updates
 * before sending the message. This ensures streaming works from the very first message.
 */
export async function sendMessageWithStreaming(
  request: SendMessageRequest,
  callbacks: StreamCallbacks
): Promise<SendMessageResponse> {
  let sessionId = request.session_id
  let eventSource: EventSource | null = null
  let createdNewSession = false

  // If no session_id, create one first so we can connect to SSE
  if (!sessionId) {
    try {
      callbacks.onStatus?.('Starting new conversation...')
      const sessionResponse = await createSession()
      sessionId = sessionResponse.session_id
      createdNewSession = true
    } catch (e) {
      console.warn('Failed to create session, falling back to non-streaming:', e)
      // Fall back to regular request without streaming
      try {
        const response = await sendMessage(request)
        callbacks.onComplete?.(response)
        return response
      } catch (error) {
        callbacks.onError?.(error instanceof Error ? error : new Error(String(error)))
        throw error
      }
    }
  }

  // Connect to SSE for real-time events
  try {
    const token = localStorage.getItem('neuro-ha-auth')
      ? JSON.parse(localStorage.getItem('neuro-ha-auth')!).state?.token
      : null

    const sseUrl = `/api/v1/chat/events?session_id=${encodeURIComponent(sessionId)}${
      token ? `&token=${encodeURIComponent(token)}` : ''
    }`

    eventSource = new EventSource(sseUrl)

    // Handle different event types from backend
    eventSource.onmessage = (event) => {
      try {
        const streamEvent: StreamEvent = JSON.parse(event.data)
        handleStreamEvent(streamEvent, callbacks)
      } catch (e) {
        console.warn('Failed to parse SSE event:', e)
      }
    }

    // Also listen for specific event types
    // Note: 'error' is NOT included here - EventSource 'error' events are connection errors
    // with no .data property and should be handled by onerror, not as named events
    const eventTypes = [
      'status', 'thought', 'tool_call_start', 'tool_call_progress',
      'tool_call_result', 'response_chunk', 'response_complete'
    ]

    eventTypes.forEach(eventType => {
      eventSource!.addEventListener(eventType, (event: MessageEvent) => {
        try {
          const streamEvent: StreamEvent = JSON.parse(event.data)
          handleStreamEvent({ ...streamEvent, type: eventType }, callbacks)
        } catch (e) {
          console.warn(`Failed to parse ${eventType} event:`, e)
        }
      })
    })

    eventSource.onerror = (e) => {
      console.warn('SSE connection error:', e)
      // Don't call onError here - the POST request is the source of truth
    }
  } catch (e) {
    console.warn('Failed to establish SSE connection:', e)
  }

  try {
    // Send the actual chat message via POST, using our session_id
    const response = await sendMessage({
      ...request,
      session_id: sessionId,
    })

    // Close SSE connection after POST completes
    if (eventSource) {
      eventSource.close()
    }

    // If we created a new session, the response will have our session_id
    // but we should still pass through the one we created
    if (createdNewSession) {
      response.session_id = sessionId
    }

    callbacks.onComplete?.(response)
    return response
  } catch (error) {
    if (eventSource) {
      eventSource.close()
    }
    callbacks.onError?.(error instanceof Error ? error : new Error(String(error)))
    throw error
  }
}

function handleStreamEvent(event: StreamEvent, callbacks: StreamCallbacks): void {
  const { type, data } = event

  switch (type) {
    case 'status':
      callbacks.onStatus?.(String(data.message || data.status || ''))
      break

    case 'thought':
      callbacks.onThought?.(String(data.thought || data.content || ''))
      break

    case 'tool_call_start':
      callbacks.onToolStart?.(
        String(data.tool || data.name || ''),
        (data.args || data.arguments || {}) as Record<string, unknown>
      )
      break

    case 'tool_call_progress':
      // Progress updates for long-running tools
      callbacks.onStatus?.(String(data.message || `Running ${data.tool}...`))
      break

    case 'tool_call_result':
      callbacks.onToolResult?.(
        String(data.tool || data.name || ''),
        data.result,
        data.success !== false
      )
      break

    case 'response_chunk':
      callbacks.onResponseChunk?.(String(data.chunk || data.content || ''))
      break

    case 'response_complete':
      // Handled by POST response
      break

    case 'error':
      callbacks.onError?.(new Error(String(data.message || data.error || 'Unknown error')))
      break

    default:
      // Log unknown event types for debugging
      console.debug('Unknown stream event:', type, data)
  }
}

// Legacy streaming function - kept for compatibility
export function streamMessage(
  request: SendMessageRequest,
  onMessage: (content: string) => void,
  onToolCall: (toolCall: ToolCall) => void,
  onDone: () => void,
  onError: (error: Error) => void
): () => void {
  let cancelled = false

  sendMessageWithStreaming(request, {
    onResponseChunk: onMessage,
    onToolStart: (name, args) => {
      onToolCall({ name, arguments: args, status: 'running' } as ToolCall & { status: string })
    },
    onToolResult: (name, result) => {
      onToolCall({ name, arguments: {}, result, status: 'success' } as ToolCall & { status: string })
    },
    onComplete: () => {
      if (!cancelled) onDone()
    },
    onError: (error) => {
      if (!cancelled) onError(error)
    },
  }).catch(() => {
    // Error handled via callback
  })

  return () => {
    cancelled = true
  }
}
